import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../store/hooks/StoreHooks';
import useCollapseFacade from '../../../../facade/collapse.Facade';
import Accordion from '../../../common/accordion';
import EquipmentTableAddChargePump from '../../../common/equipment-table-charge-pump';
import LABELS from '../../../../constants/labels/schematics.labels';
import useCalculationFacade from '../../../../facade/calculation/calculation.facade';
import useSchematicFacade from '../../../../facade/calculation/calculation-output/schematic.facade';
import Select from '../../../common/select';
import TextShortInput from '../../../common/TextShortInput';

const AccessoriesBelowEsp: React.FC = () => {
    const { accessoriesBelowEspCollapse } = useAppSelector(
        (state) => state?.collapse
    );
    const { EquipmentsBelowESP, ESPEquipmentsForBelow } = useAppSelector(state => state?.schematic);
    const { updateAccessoriesBelowEspCollapse } = useCollapseFacade();
    const { updateESPEquipmentsForBelowESP } = useSchematicFacade();
    const { updateSchematicEquipmentData, updateSchematicOtherData } = useCalculationFacade();

    const { tab1Input } = useAppSelector(state => state?.tubing);
    const { schematicData } = useAppSelector(state => state?.calculations);

    let initialTailPipeLength: number = 31.7;
    const [tailPipeDetails, setTailPipeDetails] = useState<any[]>([
        { joints: schematicData?.miscellaneous?.tailPipeJoints, length: initialTailPipeLength, weight: "--", diameter: '', setDepth: 0 },
    ]);

    useEffect(() => {
        if (schematicData) {
            const totalJoints = schematicData?.miscellaneous?.tailPipeJoints;
            setTailPipeDetails([{
                ...tailPipeDetails?.[0],
                diameter: schematicData?.miscellaneous?.tailPipeDiameter,
                length: totalJoints === 0 ? 0 : ((schematicData?.miscellaneous?.tailPipeJoints || 0) * initialTailPipeLength).toFixed(2),
                weight: schematicData?.miscellaneous?.tailPipeWeight?.toFixed(2),
                setDepth: schematicData?.miscellaneous?.tailPipeSetDepth != null ? +schematicData?.miscellaneous?.tailPipeSetDepth.toFixed(2) : null
            }]);
        }
    }, [schematicData]);

    const handleCollapseChange = (key: boolean) => {
        updateAccessoriesBelowEspCollapse(key);
    };

    const handleEquipments = (rows: any, rowIndex: number) => {
        updateESPEquipmentsForBelowESP([rows]);
        updateSchematicEquipmentData();
    };

    const handleClear = () => {
        updateESPEquipmentsForBelowESP([]);
        updateSchematicEquipmentData();
    };

    const handleSelectChange = (value: any, name: string) => {
        let modifiedValue = name === "joints" ? value?.target?.value : "";
        if (name === "diameter") {
            setTailPipeDetails([{
                ...tailPipeDetails[0],
                diameter: value?.value,
            }])
        } else if (name === "joints") {
            if (modifiedValue === "") {
                setTailPipeDetails([{
                    ...tailPipeDetails[0],
                    joints: "",
                    length: "--",
                    weight: "--",
                    setDepth: 0,
                    diameter: ""
                }]);
            } else {
                if (modifiedValue.includes('.')) {
                    const [intPart, decimalPart] = modifiedValue.split('.');
                    if (decimalPart.length > 2) {
                        modifiedValue = `${intPart}.${decimalPart.slice(0, 2)}`;
                    }
                }
                setTailPipeDetails([{
                    ...tailPipeDetails[0],
                    joints: modifiedValue,
                    length: +(modifiedValue * initialTailPipeLength).toFixed(2),
                }]);
            }
        } else {
            setTailPipeDetails([{
                ...tailPipeDetails[0],
                joints: value?.target.value,
            }])
        }
        const misc = {
            tailPipeDiameter: name === "diameter" ? value?.value : tailPipeDetails[0]?.diameter,
            tailPipeSetDepth: tailPipeDetails[0]?.setDepth,
            tailPipeJoints: name === "joints" ? +modifiedValue : +tailPipeDetails[0]?.joints,
            tailPipeWeight: +(name === "joints" ? (modifiedValue * initialTailPipeLength * tab1Input?.tubingWeight)?.toFixed(2) : (tailPipeDetails[0]?.joints * tab1Input?.tubingWeight * initialTailPipeLength)?.toFixed(2)),
        };
        updateSchematicOtherData(misc);
        updateSchematicEquipmentData();
    };

    return (
        <Accordion
            title={LABELS?.accessoriesBelowESP}
            isOpen={accessoriesBelowEspCollapse}
            onToggle={() => handleCollapseChange(!accessoriesBelowEspCollapse)}
            content={
                <>
                    <div className='flex flex-col gap-[16px] p-4'>
                        {EquipmentsBelowESP?.length > 0 &&
                            <EquipmentTableAddChargePump
                                options={EquipmentsBelowESP}
                                equipments={ESPEquipmentsForBelow?.length > 0 ? ESPEquipmentsForBelow : [{}]}
                                rowLength={1}
                                onChange={handleEquipments}
                                onClear={handleClear}
                                equipmentGridParentContainer="SRAccBelowESP"
                            />
                        }
                    </div>
                    <div className={`scrollable relative overflow-auto m-4 mt-0`}>
                        <table className='min-w-full rounded-[8px] outline-[1px] table'>
                            <thead style={{ background: "#253040" }}>
                                <th className='px-[24px] py-[16px] text-[14px] text-neutral-25'>
                                    <div className="flex items-center">Total Joints</div>
                                </th>
                                <th className='px-[24px] py-[16px] text-[14px] text-neutral-25'>
                                    <div className="flex items-center">Length (ft)</div>
                                </th>
                                <th className='px-[24px] py-[16px] text-[14px] text-neutral-25'>
                                    <div className="flex items-center">Weight (lbs)</div>
                                </th>
                                <th className='px-[24px] py-[16px] text-[14px] text-neutral-25'>
                                    <div className="flex items-center">Diameter (in)</div>
                                </th>
                                <th className='px-[24px] py-[16px] text-[14px] text-neutral-25'>
                                    <div className="flex items-center">Set depth (ft)</div>
                                </th>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px] p-4'>
                                        <TextShortInput
                                            className={`flex-grow py-[12px] px-[14px] max-h-[44px] text-neutral-25 focus:outline-none bg-transparent rounded-[8px] border-[1px] border-darkcolor-400 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500`}
                                            onChange={(e) => handleSelectChange(e, "joints")}
                                            value={tailPipeDetails?.[0]?.joints}
                                            placeholder={"Joints"}
                                            type={"number"}
                                            leftAlign={true}
                                            min={0}
                                        />
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px] p-4'>
                                        {tailPipeDetails[0]?.length}
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px] p-4'>
                                        {tailPipeDetails[0]?.joints ? (tab1Input?.tubingWeight ? `${(tailPipeDetails[0]?.joints * initialTailPipeLength * tab1Input?.tubingWeight)?.toFixed(2)}` : "--") : "--"}
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px] p-4'>
                                        <Select
                                            options={tab1Input?.tubingODList || []}
                                            onChange={(e) => handleSelectChange(e, "diameter")}
                                            padding="px-[14px] py-[5px]"
                                            fontSize="16px"
                                            className="text-neutral-25"
                                            selectedValue={tailPipeDetails[0]?.diameter}
                                            name={"MotorDeratingPercent"}
                                        />
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px] p-4'>
                                        {tailPipeDetails[0]?.setDepth}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            }
        />
    );
}

export default AccessoriesBelowEsp